"use client";

import { ExternalLink, Eye } from "lucide-react";
import Link from "@/components/ui/Link";
import { TimeAgo } from "@/components/ui/TimeAgo";
import { FavoriteCamButton } from "./FavoriteCamButton";
import type { Stream } from "@/lib/api/types";
import { thumbBackground } from "@/lib/art";
import { cn } from "@/lib/cn";
import { compact } from "@/lib/format";
import { platformBySource } from "@/lib/stream-platforms";
import { useUi } from "@/components/ui/use-ui";
import { fmt, rich } from "@/lib/locales/fmt";

/** Streams play on the site that hosts them, in a new tab. */
const external = { target: "_blank", rel: "noopener nofollow sponsored" } as const;

export function LiveBadge({ className }: { className?: string }) {
  const t = useUi();
  return (
    <span
      className={cn(
        "flex items-center gap-1.5 rounded-md bg-brand-strong px-1.5 py-[3px] text-[11px] font-semibold uppercase leading-none tracking-wider text-white shadow-[0_2px_10px_rgb(255_46_126/0.45)]",
        className,
      )}
    >
      <LiveDot />
      {t.cams.live}
    </span>
  );
}

export function LiveDot({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cn("relative flex size-1.5", className)}>
      <span className="absolute inline-flex size-full animate-ping rounded-full bg-current opacity-75 motion-reduce:animate-none" />
      <span className="relative inline-flex size-full rounded-full bg-current" />
    </span>
  );
}

/**
 * A 16:9 snapshot (live badge, viewers, "Watch on …" on hover) over one line: the name and how long
 * they've been live. The snapshot opens the room on the cam site; the name, the model's page here.
 */
/**
 * `place`: where on the site the card is, for the click counters (lib/stats.ts). `priority`: among the
 * first on screen, so its snapshot is fetched ahead of the rest.
 */
export function StreamCard({
  stream,
  eager,
  priority,
  className,
  place = "grid",
}: {
  stream: Stream;
  eager?: boolean;
  priority?: boolean;
  className?: string;
  place?: string;
}) {
  const t = useUi();
  const title = stream.title || fmt(t.cams.isLive, { name: stream.performerName });
  const platform = platformBySource(stream.source);
  const site = platform?.name ?? stream.source;
  return (
    <article className={cn("group/card min-w-0", className)}>
      <a
        href={stream.url}
        {...external}
        data-stat={platform ? `room:${platform.id}:${place}` : undefined}
        aria-label={fmt(t.cams.watchOn, { title, site })}
        className="relative block aspect-video overflow-hidden rounded-lg bg-surface ring-1 ring-white/[0.04]"
      >
        {stream.thumb ? (
          // eslint-disable-next-line @next/next/no-img-element -- snapshots come from the stream host
          <img
            src={stream.thumb}
            alt=""
            loading={eager || priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : undefined}
            decoding="async"
            className="size-full object-cover transition-transform duration-500 group-hover/card:scale-[1.03]"
          />
        ) : (
          <span
            aria-hidden
            className="block size-full transition-transform duration-[1.2s] ease-out group-hover/card:scale-110"
            style={{ backgroundImage: thumbBackground(stream.id) }}
          />
        )}
        <span aria-hidden className="absolute inset-x-0 top-0 h-12 bg-linear-to-b from-black/45 to-transparent" />
        <LiveBadge className="absolute left-2 top-2" />
        {stream.viewers !== null && (
          <span className="thumb-text absolute right-2.5 top-2 flex items-center gap-1 text-[13px] tracking-wide text-white">
            <Eye size={14} strokeWidth={2} aria-hidden />
            <span className="sr-only">{t.cams.viewers}</span>
            {compact(stream.viewers)}
          </span>
        )}
        <span className="theme-dark glass absolute bottom-2 right-2 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] text-fg opacity-0 transition-opacity duration-200 group-hover/card:opacity-100 group-focus-within/card:opacity-100">
          {fmt(t.cams.watchOnSite, { site })}
          <ExternalLink size={12} aria-hidden />
        </span>
      </a>

      <p className="mt-2 flex items-baseline gap-2 leading-snug">
        {platform ? (
          <Link href={`/cams/${platform.id}/${encodeURIComponent(stream.username)}`} className="min-w-0 truncate text-[14px] text-fg transition-colors hover:text-brand-soft" title={title}>
            {stream.performerName}
          </Link>
        ) : (
          <a href={stream.url} {...external} tabIndex={-1} className="min-w-0 truncate text-[14px] text-fg transition-colors hover:text-brand-soft" title={title}>
            {stream.performerName}
          </a>
        )}
        {stream.onlineTime ? (
          <span className="shrink-0 text-[12px] text-muted">{fmt(t.cams.liveFor, { time: stream.onlineTime })}</span>
        ) : (
          stream.startedAt && (
            <span className="shrink-0 text-[12px] text-muted">{rich(t.cams.liveFor, { time: <TimeAgo iso={stream.startedAt} /> })}</span>
          )
        )}
        <FavoriteCamButton source={stream.source} username={stream.username} name={stream.performerName} avatar={stream.performerAvatar ?? stream.thumb} className="-my-1 ml-auto self-center" />
      </p>
    </article>
  );
}

/** A stream card while it loads. */
export function StreamCardSkeleton() {
  return (
    <div aria-hidden className="min-w-0">
      <div className="skeleton aspect-video rounded-lg" />
      <div className="skeleton mt-2.5 h-3.5 w-3/5 rounded" />
    </div>
  );
}
